import React from 'react';
import { startCase, camelCase, get, mapValues } from 'lodash';
import { Col } from 'antd';
import dayjs from 'dayjs';
import UseApi from '../../api';
import { required, required as requiredRule } from '../../utils/rules';
import {
  AdFormatType,
  CreativeType,
  Status,
  Status2,
  UserType,
} from '../../constants/Enums';
import FormInput from '../../components/FormInput';
import FormSelect, { FormSelectProps } from '../FormSelect';
import FormSearch, { FormSearchProps } from '../FormSearch';
import FormDate, { FormRangePickerProps } from '../FormDate';

const dateRangeMap = {
  Today: '0_day_day:0_day_day',
  Yesterday: '1_day_day:1_day_day',
  'This Week': '0_day_week:0_day_day',
  'Last Week': '1_week_week:1_week_week',
  'This Month': '0_day_month:0_day_day',
  'Last Month': '1_month_month:1_month_month',
  'Last 7 Days': '7_day_day:0_day_day',
  'Last 30 Days': '30_day_day:0_day_day',
};
const dateRangeOptions = Object.entries(dateRangeMap).map(([label, value]) => ({ label, value }));

function parseDateRangeString(range: string) {
  /** @type {[DateArr, DateArr]} */
  // @ts-ignore
  const [fromArr, toArr] = range.split(':').map((item) => item.split('_'));

  return {
    //@ts-ignore
    fromDate: dayjs().subtract(Number(fromArr[0]), fromArr[1]).startOf(fromArr[2]).toDate(),
    //@ts-ignore
    toDate: dayjs().subtract(Number(toArr[0]), toArr[1]).endOf(toArr[2]).toDate(),
  };
}

const FormDateRanges = mapValues(dateRangeMap, (v) =>
  Object.values(parseDateRangeString(v)).map(dayjs),
);

type SeachOpts = Partial<Omit<FormSearchProps, 'name' | 'api' | 'label'>>;

function getIdSearchComponent(labelKey: string, searchProps: SeachOpts & { api?: string } = {}) {
  const defaultName = `${camelCase(labelKey)}Id`;
  const defaultLabel = startCase(labelKey);
  const {
    api = `${labelKey}.Search`,
    tooltip = `Search by ${defaultLabel} name or ID`,
    ...restSearchProps
  } = searchProps;

  const useApi = get(UseApi, api);
  if (!useApi) throw new Error('Invalid search api');

  return (
    props: {
      name?: string;
      label?: string;
      key: string;
    } & SeachOpts,
  ) => {
    const { name = defaultName, label = defaultLabel, key, ...rest } = props;

    return (
      <Col xs={24} sm={12} md={8} key={key}>
        <FormSearch
          name={['filters', name]}
          label={`${label} Filter`}
          placeholder={`Select ${label}`}
          tooltip={label === defaultLabel ? tooltip : `Search by ${label} name or ID`}
          api={useApi}
          {...restSearchProps}
          {...rest}
        />
      </Col>
    );
  };
}

type SelectOpts = Partial<Omit<FormSelectProps, 'name' | 'label'>>;

function getTypeSelectComponent(defaultName: string, selectProps: SelectOpts = {}) {
  const { options: defaultOpts = [] } = selectProps;
  return (
    props: {
      name?: string;
      label?: string;
      key: string;
    } & SelectOpts,
  ) => {
    const {
      name = defaultName,
      label = startCase(name),
      options = defaultOpts,
      key,
      ...rest
    } = props;

    return (
      <Col xs={24} sm={12} lg={8} key={key}>
        <FormSelect
          name={['filters', name]}
          label={label}
          placeholder={`Select ${label}`}
          options={options}
          {...selectProps}
          {...rest}
        />
      </Col>
    );
  };
}

const filterTypes = {
  date: {
    component: ({
      key,
      extra,
      initialValue,
    }: {
      key: string;
      extra?: any;
      initialValue?: FormRangePickerProps['initialValue'];
    }) => (
      <Col xs={24} sm={12} md={8} key={key}>
        <FormDate
          rules={extra?.required ? [requiredRule] : []}
          name="dateRange"
          label="From Date → To Date"
          type="range"
          initialValue={extra?.required ? initialValue : undefined}
          {...(extra?.includeRanges
            ? {
              datePickerProps: {
                ranges: FormDateRanges,
              },
            }
            : {})}
          {...extra}
        />
      </Col>
    ),
  },
  dateRange: {
    default: '7_day_day:0_day_day', // Last 7 Days
    component: getTypeSelectComponent('dateRange', {
      options: dateRangeOptions,
      rules: [required],
      selectProps: { allowClear: false },
    }),
  },
  status: {
    default: 'ACTIVE',
    component: getTypeSelectComponent('status', { options: Status }),
  },
  status2: {
    component: getTypeSelectComponent('status', { options: Status2 }),
  },
  userType: {
    component: getTypeSelectComponent('userType', { options: UserType }),
  },
  creativeType: {
    component: getTypeSelectComponent('creativeType', { options: CreativeType }),
  },
  exchange: {
    component: getIdSearchComponent('Exchange'),
  },
  adFormat: {
    component: getTypeSelectComponent('adFormat', { options: AdFormatType }),
  },
  orientation: {
    component: getTypeSelectComponent('orientation', { options: ['PORTRAIT', 'LANDSCAPE'] }),
  },
  advertiserId: {
    default: [],
    component: getIdSearchComponent('Advertiser'),
  },
  userId: {
    default: [],
    component: getIdSearchComponent('User'),
  },
  campaignId: {
    default: [],
    component: getIdSearchComponent('Campaign'),
  },
  appId: {
    default: [],
    component: getIdSearchComponent('AppAsset'),
  },
  appListId: {
    default: [],
    component: getIdSearchComponent('AppList'),
  },
  audienceId: {
    default: [],
    component: getIdSearchComponent('Audience'),
  },
  ipListId: {
    default: [],
    component: getIdSearchComponent('IpList'),
  },
  orderId: {
    default: [],
    component: getIdSearchComponent('Order'),
  },
  creativeId: {
    default: [],
    component: getIdSearchComponent('Creative'),
  },
  trackerId: {
    default: [],
    component: getIdSearchComponent('Tracker'),
  },
  exchangeId: {
    default: [],
    component: getIdSearchComponent('Exchange'),
  },
  phrase: {
    component: ({
      label,
      key,
      initialValue,
    }: {
      label: string;
      key: string;
      initialValue?: string;
    }) => (
      <Col xs={24} sm={12} md={8} key={key}>
        <FormInput
          name="phrase"
          label={`${startCase(label)} Search`}
          tooltip="Search by any phrase"
          placeholder="Search by any phrase"
          formItemProps={{ initialValue }}
        />
      </Col>
    ),
  },
};

type FilterTypes = typeof filterTypes;

export type FilterItemProps<T extends keyof FilterTypes> = Omit<
  Parameters<FilterTypes[T]['component']>[0],
  'key'
>;

type DateFilterProps = FilterItemProps<'date'> & { oldFromDate?: boolean };

export type FiltersMap = Partial<{
  [T in keyof FilterTypes]: T extends 'phrase'
    ? FilterItemProps<T>
    : boolean | (T extends 'date' ? DateFilterProps : FilterItemProps<T>);
}>;

export default filterTypes;
export { dateRangeMap, parseDateRangeString };
